import { toast } from 'sonner'
import { formatTokens } from './ai'

/** Share of tokens left at which the agent is warned, largest first. */
const THRESHOLDS = [0.1, 0.05, 0.01]

const key = (reference: number, threshold: number) => `ai-low-balance:${reference}:${threshold}`

/**
 * What "Top up" does. The billing provider sets it to its in-app navigation,
 * so following it does not reload the page and lose a half-filled form; a
 * full navigation is only the fallback before that is set.
 */
let openTopUp: () => void = () => window.location.assign('/pricing?tab=credits')

export function setLowBalanceTopUp(handler: (() => void) | null) {
    openTopUp = handler ?? (() => window.location.assign('/pricing?tab=credits'))
}

/**
 * Warns once each time the balance falls to 10%, 5% and 1% of what it was
 * after the last top-up or renewal. A new top-up or renewal changes the
 * reference, so the warnings start again from there. Remembered per browser.
 */
export function warnIfLow(tokensLeft: number | null | undefined, reference: number | null | undefined) {
    const left = Number(tokensLeft)
    const total = Number(reference)
    if (!Number.isFinite(left) || !Number.isFinite(total) || total <= 0 || left < 0) return
    const share = left / total
    // The tightest threshold reached is the one to show.
    const crossed = [...THRESHOLDS].reverse().find((t) => share <= t)
    if (crossed === undefined) return
    try {
        if (localStorage.getItem(key(total, crossed))) return
        // Showing 1% also covers 5% and 10%, so those are not shown after it.
        THRESHOLDS.filter((t) => t >= crossed).forEach((t) => localStorage.setItem(key(total, t), '1'))
    } catch {
        // Without storage the warning may repeat; better than never showing.
    }
    const percent = Math.max(0, Math.round(share * 100))
    const message = left <= 0 ? 'You have used all your AI tokens.' : `${percent < 1 ? 'Less than 1%' : `${percent}%`} of your AI tokens left (${formatTokens(left)}).`
    const show = crossed <= 0.01 ? toast.error : toast.warning
    show(message, {
        description: 'Top up or change plan to keep using AI.',
        duration: 10000,
        action: { label: 'Top up', onClick: () => openTopUp() },
    })
}
